import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { BookmarkSimple, X } from "@phosphor-icons/react";
import { Link, useLocation } from "wouter";
import type { Decision, Job } from "../../shared/types.ts";
import {
  Button,
  Card,
  Count,
  EmptyState,
  IconButton,
  Kbd,
  MarkLoader,
  Monogram,
  PageHeader,
  Segmented,
  StatusBadge,
  Steps,
  cn,
  ease,
  spring,
} from "../ui/index.ts";
import { getFeed } from "../lib/api.ts";
import { useApp } from "../lib/ctx.ts";
import { ago, greeting, pay, place, SENIORITY, WORKPLACE } from "../lib/format.ts";
import { Detail } from "./Detail.tsx";
import { NeedsYou } from "./NeedsYou.tsx";
import { SentList } from "./SentList.tsx";
import { applyLabel, SendButton, useDecide } from "./parts.tsx";
import { dirOf, JobFlags, lede, SwipeCard, Via, type Motion } from "./SwipeCard.tsx";

const STEP_LABELS = ["Contact and resume", "What you want", "Standing answers"];
const MODE_KEY = "headcount.today.mode";
type Mode = "list" | "swipe";

function storedMode(): Mode {
  try {
    return localStorage.getItem(MODE_KEY) === "swipe" ? "swipe" : "list";
  } catch {
    return "list";
  }
}

export function Home() {
  const app = useApp();
  const name = app.profile?.preferredName || app.profile?.firstName;
  const ob = app.onboarding;
  const [mode, setMode] = useState<Mode>(storedMode);
  const pick = (m: Mode) => {
    setMode(m);
    try {
      localStorage.setItem(MODE_KEY, m);
    } catch {}
  };
  return (
    <div className="flex flex-col gap-6 md:gap-8">
      <PageHeader
        title={greeting(name)}
        description={<AgentLine />}
        actions={
          ob?.done && (
            <div onPointerUp={() => (document.activeElement as HTMLElement | null)?.blur()}>
              <Segmented<Mode>
                aria-label="View"
                value={mode}
                onChange={pick}
                options={[
                  { value: "list", label: "List" },
                  { value: "swipe", label: "Swipe" },
                ]}
              />
            </div>
          )
        }
      />
      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_300px] xl:gap-8">
        <div className="flex min-w-0 flex-col gap-6">
          <NeedsYou rows={app.needs} onSent={app.needsSent} limit={2} className="lg:hidden" />
          {!ob ? <MarkLoader label="Loading your matches" /> : ob.done ? <Feed mode={mode} /> : <Gate step={ob.step} missing={ob.missing} />}
        </div>
        <aside className="-m-2 hidden flex-col gap-6 p-2 [scrollbar-width:thin] lg:sticky lg:top-8 lg:flex lg:max-h-[calc(100dvh-4rem)] lg:overflow-y-auto lg:overscroll-contain">
          <NeedsYou rows={app.needs} onSent={app.needsSent} limit={2} />
          <SentList seeAll />
          {ob?.done && mode === "swipe" ? <Keys /> : null}
        </aside>
      </div>
    </div>
  );
}

/** One line under the greeting: what went out today and what is on its way. */
function AgentLine() {
  const app = useApp();
  const a = app.stats?.applications;
  if (app.onboarding && !app.onboarding.done) return <>Finish your profile and Headcount starts applying for you.</>;
  const today = app.stats?.appliedToday ?? 0;
  const sending = (a?.pending ?? 0) + (a?.queued ?? 0) + (a?.applying ?? 0);
  const parts = [today ? `${today} applied today` : null, sending ? `${sending} sending` : null].filter(Boolean);
  if (!parts.length) return <>Fresh matches, ranked for you.</>;
  const line = parts.join(", ");
  return <>{line[0]!.toUpperCase() + line.slice(1)}.</>;
}

function Keys() {
  const keys: [string, string][] = [
    ["→", "Apply"],
    ["←", "Pass"],
    ["↑", "Save"],
    ["Enter", "Open the posting"],
    ["Z", "Undo"],
  ];
  return (
    <Card className="p-4">
      <h2 className="text-[15px] font-bold tracking-[-0.01em] text-ink">Keys</h2>
      <dl className="mt-3 flex flex-col gap-2.5">
        {keys.map(([k, label]) => (
          <div key={k} className="flex items-center justify-between gap-3 text-[13px]">
            <dt className="text-ink-soft">{label}</dt>
            <dd>
              <Kbd keys={k} />
            </dd>
          </div>
        ))}
      </dl>
    </Card>
  );
}

const MISSING: Record<string, string> = { resume: "Resume", phone: "Phone", firstName: "Name", lastName: "Name", name: "Name", email: "Email" };

function Gate({ step, missing }: { step: number; missing: string[] }) {
  const [, navigate] = useLocation();
  const gaps = [...new Set(missing.map((m) => MISSING[m] ?? m))];
  return (
    <motion.section initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={spring}>
      <Card className="grid overflow-hidden md:grid-cols-[minmax(0,1fr)_260px]">
        <div className="flex flex-col gap-6 p-6 md:p-8">
          <div className="space-y-2">
            <StatusBadge tone="amber" dot>
              Step {Math.min(step + 1, 3)} of 3
            </StatusBadge>
            <h2 className="max-w-[24ch] font-display text-[26px] leading-[1.15] text-ink">Finish your profile so Headcount can apply for you.</h2>
          </div>
          <Steps steps={STEP_LABELS.map((label, i) => ({ label, state: i < step ? "done" : i === step ? "active" : "todo" }))} />
          <Button className="self-start" size="lg" onClick={() => navigate("/welcome")}>
            Finish your profile
          </Button>
        </div>
        <div className="flex flex-col gap-3 border-t border-line bg-cream-soft p-6 md:border-l md:border-t-0">
          <h3 className="text-[13px] font-semibold text-cream-ink">Still needed</h3>
          {gaps.length ? (
            <ul className="flex flex-col gap-2">
              {gaps.map((g) => (
                <li key={g} className="flex items-center gap-2 text-sm font-semibold text-ink">
                  <span aria-hidden className="size-1.5 rounded-full bg-accent" />
                  {g}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-ink-soft">{STEP_LABELS[Math.min(step, 2)]}</p>
          )}
          <p className="mt-2 text-[13px] text-cream-ink">Headcount uses these on every form it fills.</p>
        </div>
      </Card>
    </motion.section>
  );
}

const UNDO_TEXT: Record<Decision, string> = { pass: "Passed.", save: "Saved.", apply: "Opened on the company site." };

type Done = { job: Job; decision: Decision; index: number };

/** An apply Headcount sends, answered on its button: `send` records it, `sent` takes the card out after the check. */
type Sender = { send: (job: Job, from: Element) => Promise<boolean>; sent: (job: Job) => void };

function Feed({ mode }: { mode: Mode }) {
  const app = useApp();
  const decideJob = useDecide();
  const [deck, setDeck] = useState<Job[]>([]);
  const [phase, setPhase] = useState<"loading" | "ready" | "error">("loading");
  const [exhausted, setExhausted] = useState(false);
  const [history, setHistory] = useState<Done[]>([]);
  const [motionState, setMotionState] = useState<Motion>({ dir: 0, back: null, gone: null });
  const [open, setOpen] = useState<Job | null>(null);
  const [undoBar, setUndoBar] = useState<{ key: number; job: string; text: string; saved: boolean } | null>(null);
  const sendRef = useRef<HTMLButtonElement>(null);
  const seen = useRef(new Set<string>());
  const firstPage = useRef<Set<string> | null>(null);
  const fetching = useRef(false);

  const load = useCallback(async () => {
    if (fetching.current) return;
    fetching.current = true;
    setPhase((p) => (p === "error" ? "loading" : p));
    try {
      const r = await getFeed(20, [...seen.current].slice(-200));
      const fresh = r.items.filter((j) => !seen.current.has(j.id));
      for (const j of fresh) seen.current.add(j.id);
      firstPage.current ??= new Set(fresh.map((j) => j.id));
      if (!fresh.length) setExhausted(true);
      setDeck((d) => [...d, ...fresh]);
      setPhase("ready");
    } catch {
      setPhase((p) => (p === "loading" ? "error" : p));
    } finally {
      fetching.current = false;
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (phase === "ready" && !exhausted && deck.length < 6) load();
  }, [deck.length, phase, exhausted, load]);

  useEffect(() => {
    if (!undoBar) return;
    const t = setTimeout(() => setUndoBar(null), 6000);
    return () => clearTimeout(t);
  }, [undoBar]);

  /** Puts a card back where it was, for an undo or a decision that didn't stick. */
  const putBack = useCallback(
    ({ job, decision, index }: Done) => {
      setHistory((h) => h.filter((x) => x.job.id !== job.id));
      setMotionState({ dir: 0, back: dirOf(decision), gone: null });
      setDeck((d) => {
        const rest = d.filter((x) => x.id !== job.id);
        const at = mode === "swipe" ? 0 : Math.min(index, rest.length);
        return [...rest.slice(0, at), job, ...rest.slice(at)];
      });
      setUndoBar((b) => (b?.job === job.id ? null : b));
    },
    [mode],
  );

  const takeOut = useCallback(
    (job: Job, decision: Decision): Done => {
      const done = { job, decision, index: Math.max(0, deck.findIndex((x) => x.id === job.id)) };
      setMotionState({ dir: dirOf(decision), back: null, gone: job.id });
      setDeck((d) => d.filter((x) => x.id !== job.id));
      setHistory((h) => [...h.slice(-19), done]);
      return done;
    },
    [deck],
  );

  const act = useCallback(
    (decision: Decision, job: Job | undefined = deck[0], from?: Element | null) => {
      if (!job) return;
      const done = takeOut(job, decision);
      setOpen(null);
      // A swiped apply Headcount sends leaves without a bar; the card comes back if a limit stops it.
      const sends = decision === "apply" && job.canAutoApply;
      setUndoBar(sends ? null : { key: Date.now(), job: job.id, text: UNDO_TEXT[decision], saved: decision === "save" });
      decideJob(job, decision, null, from ?? null, true).then((m) => {
        if (m !== decision) return putBack(done);
        if (decision === "save") app.refreshStats();
      });
    },
    [deck, takeOut, decideJob, app, putBack],
  );

  const sender = useMemo<Sender>(
    () => ({
      send: (job, from) => decideJob(job, "apply", null, from, true).then((m) => m === "apply"),
      sent: (job) => {
        takeOut(job, "apply");
        setUndoBar(null);
      },
    }),
    [decideJob, takeOut],
  );

  /** The detail sheet sent or took back an apply itself; the card follows without closing the sheet. */
  const applied = (job: Job, on: boolean) => {
    if (on) return sender.sent(job);
    putBack(history.find((h) => h.job.id === job.id) ?? { job, decision: "apply", index: 0 });
  };

  const undo = useCallback(() => {
    const last = history.at(-1);
    if (!last) return;
    putBack(last);
    setUndoBar(null);
    decideJob(last.job, last.decision, last.decision, null, true).then(() => last.decision === "save" && app.refreshStats());
  }, [history, decideJob, app, putBack]);

  useEffect(() => {
    if (mode !== "swipe") return;
    const k = (e: KeyboardEvent) => {
      if (open || e.metaKey || e.ctrlKey || e.altKey) return;
      if ((e.target as HTMLElement).closest("input,textarea,select,button,a,[role=dialog],[role=radiogroup]")) return;
      const map: Record<string, () => void> = {
        ArrowRight: () => (deck[0]?.canAutoApply ? sendRef.current?.click() : act("apply")),
        ArrowLeft: () => act("pass"),
        ArrowUp: () => act("save"),
        s: () => act("save"),
        z: undo,
        Backspace: undo,
        Enter: () => deck[0] && setOpen(deck[0]),
        " ": () => deck[0] && setOpen(deck[0]),
      };
      const f = map[e.key];
      if (f) {
        e.preventDefault();
        f();
      }
    };
    addEventListener("keydown", k);
    return () => removeEventListener("keydown", k);
  }, [mode, act, undo, deck, open]);

  const empty = !deck.length;

  if (phase === "loading" && empty) return <MarkLoader label="Loading your matches" />;

  if (phase === "error" && empty)
    return (
      <Card>
        <EmptyState
          title="Your matches didn't load."
          action={
            <Button variant="secondary" onClick={load}>
              Try again
            </Button>
          }
        />
      </Card>
    );

  return (
    <div className="flex flex-col gap-3">
      {!(empty && exhausted) && (
        <div className={cn("flex h-7 items-center justify-between gap-3", mode === "swipe" && "max-md:hidden")}>
          <h2 className="flex items-center gap-2 text-[17px] font-bold tracking-[-0.015em] text-ink">
            Your matches
            <Count value={exhausted ? deck.length : `${deck.length}+`} />
          </h2>
          <p className="text-[13px] text-muted">Ranked by fit to your profile</p>
        </div>
      )}
      {empty && exhausted ? (
        <CaughtUp />
      ) : mode === "list" ? (
        <JobList jobs={deck} custom={motionState} fresh={firstPage.current} exhausted={exhausted} onAct={act} sender={sender} onOpen={setOpen} onNear={load} />
      ) : (
        <Deck jobs={deck} custom={motionState} onAct={act} sender={sender} sendRef={sendRef} onOpen={setOpen} />
      )}
      <Dock bar={undoBar} onUndo={history.length ? undo : undefined} />
      <Detail job={open} onClose={() => setOpen(null)} onAct={(d, j) => act(d, j)} onApplied={applied} />
    </div>
  );
}

const leave = ({ dir, gone }: Motion, id: string) =>
  gone !== id
    ? { opacity: 0, height: 0, transition: { duration: 0.2 } }
    : dir === 0
      ? { opacity: 0, scale: 0.97, y: -12, transition: { duration: 0.24, ease } }
      : { opacity: 0, x: dir * 120, transition: { duration: 0.26, ease } };

function JobList({
  jobs,
  custom,
  fresh,
  exhausted,
  onAct,
  sender,
  onOpen,
  onNear,
}: {
  jobs: Job[];
  custom: Motion;
  fresh: Set<string> | null;
  exhausted: boolean;
  onAct: (d: Decision, j: Job, from?: Element | null) => void;
  sender: Sender;
  onOpen: (j: Job) => void;
  onNear: () => void;
}) {
  const sentinel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = sentinel.current;
    if (!el || exhausted) return;
    const io = new IntersectionObserver((e) => e.some((x) => x.isIntersecting) && onNear(), { rootMargin: "800px 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, [exhausted, onNear, jobs.length]);

  return (
    <>
      <ul className="overflow-hidden rounded-card bg-surface shadow-card">
        <AnimatePresence custom={custom} mode="popLayout">
          {jobs.map((j, i) => (
            <JobRow key={j.id} job={j} custom={custom} order={fresh?.has(j.id) ? i : -1} onAct={onAct} sender={sender} onOpen={onOpen} />
          ))}
        </AnimatePresence>
      </ul>
      <div ref={sentinel} aria-hidden />
      {exhausted && jobs.length > 0 && (
        <p className="py-6 text-center text-sm text-muted">
          That's every new match.{" "}
          <Link href="/jobs" className="font-semibold text-primary-ink underline-offset-4 hover:underline">
            Browse all jobs
          </Link>
        </p>
      )}
    </>
  );
}

function JobRow({
  job,
  custom,
  order,
  onAct,
  sender,
  onOpen,
}: {
  job: Job;
  custom: Motion;
  order: number;
  onAct: (d: Decision, j: Job, from?: Element | null) => void;
  sender: Sender;
  onOpen: (j: Job) => void;
}) {
  const money = pay(job);
  const snippet = lede(job);
  const where = [place(job), WORKPLACE[job.workplace], job.seniority ? SENIORITY[job.seniority] : null].filter((x, i, all) => x && all.indexOf(x) === i);
  const stop = (f: (e: React.MouseEvent) => void) => (e: React.MouseEvent) => {
    e.stopPropagation();
    f(e);
  };
  return (
    <motion.li
      layout
      custom={custom}
      variants={{
        hidden: { opacity: 0, y: 12 },
        show: { opacity: 1, y: 0, x: 0, scale: 1, transition: { ...spring, delay: Math.min(Math.max(0, order), 10) * 0.03 } },
        gone: (c: Motion) => leave(c, job.id),
      }}
      initial={(order >= 0 && order < 12) || custom.back !== null ? "hidden" : false}
      animate="show"
      exit="gone"
      transition={spring}
      className="@container list-none border-b border-line bg-surface last:border-b-0"
    >
      <article
        onClick={() => onOpen(job)}
        className="group grid cursor-pointer grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-3 px-4 py-4 transition-[background-color] duration-150 hover:bg-sunk/60 @md:px-5 @xl:grid-cols-[auto_minmax(0,1fr)_auto]"
      >
        <Monogram name={job.company} url={job.jobUrl} />
        <div className="flex min-w-0 flex-col">
          <div className="flex items-baseline justify-between gap-3">
            <h3 className="min-w-0 text-[15px] font-bold leading-snug tracking-[-0.01em] text-pretty text-ink">
              <button
                type="button"
                onClick={stop(() => onOpen(job))}
                className="text-left decoration-primary decoration-2 underline-offset-4 group-hover:underline"
              >
                {job.title}
              </button>
            </h3>
            {money && <span className="hidden shrink-0 text-[15px] font-bold tabular-nums text-ink @xl:inline">{money}</span>}
          </div>
          <p className="mt-0.5 truncate text-[13px] text-ink-soft">
            <span className="font-semibold text-ink">{job.company}</span>
            {where.map((w) => (
              <span key={w}> · {w}</span>
            ))}
            {money && <span className="font-semibold text-ink @xl:hidden"> · {money}</span>}
          </p>
          {snippet && <p className="mt-1.5 hidden text-[13px] leading-relaxed text-muted @xl:line-clamp-1">{snippet}</p>}
          <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-2">
            <JobFlags job={job} />
            <span className="flex items-center gap-1.5 text-[13px] text-muted">
              <Via source={job.source} />
              {job.postedAt && (
                <>
                  <span aria-hidden>·</span>
                  {ago(job.postedAt)}
                </>
              )}
            </span>
          </div>
        </div>
        <div className="col-span-2 flex items-center justify-end gap-1 self-center @xl:col-span-1">
          <IconButton label="Pass" size="sm" onClick={stop(() => onAct("pass", job))} className="text-muted hover:bg-tone-red hover:text-bad-ink">
            <X size={18} weight="bold" aria-hidden />
          </IconButton>
          <IconButton label="Save" size="sm" onClick={stop(() => onAct("save", job))} className="text-muted hover:bg-tone-cream hover:text-cream-ink">
            <BookmarkSimple size={18} weight="bold" aria-hidden />
          </IconButton>
          {job.canAutoApply ? (
            <SendButton size="sm" className="ml-1.5 min-w-[7.5rem]" send={(el) => sender.send(job, el)} onSent={() => sender.sent(job)} />
          ) : (
            <Button size="sm" variant="secondary" className="ml-1.5 min-w-[7.5rem]" title={applyLabel(job)} onClick={stop((e) => onAct("apply", job, e.currentTarget))}>
              Apply on site
            </Button>
          )}
        </div>
      </article>
    </motion.li>
  );
}

function Deck({
  jobs,
  custom,
  onAct,
  sender,
  sendRef,
  onOpen,
}: {
  jobs: Job[];
  custom: Motion;
  onAct: (d: Decision, j?: Job, from?: Element | null) => void;
  sender: Sender;
  sendRef: React.Ref<HTMLButtonElement>;
  onOpen: (j: Job) => void;
}) {
  const top = jobs[0];
  const deckRef = useRef<HTMLDivElement>(null);
  const onSite = !!top && !top.canAutoApply;
  return (
    <div className="mx-auto flex w-full max-w-[600px] flex-col gap-4 md:gap-5">
      <div ref={deckRef} className="grid pb-4">
        <AnimatePresence custom={custom} initial={false}>
          {jobs
            .slice(0, 2)
            .reverse()
            .map((j) => (
              <SwipeCard key={j.id} job={j} custom={custom} peek={j.id !== top?.id} onDecide={(d, x) => onAct(d, x, deckRef.current)} onOpen={onOpen} />
            ))}
        </AnimatePresence>
      </div>
      <div className="grid grid-cols-[1fr_1fr_1.4fr] gap-2.5">
        <DeckAction label="Pass" keys="←" onClick={() => onAct("pass")} disabled={!top} variant="secondary" />
        <DeckAction label="Save" keys="↑" onClick={() => onAct("save")} disabled={!top} variant="secondary" />
        {top?.canAutoApply ? (
          <DeckAction key={top.id} label="Apply" keys="→" variant="primary" ref={sendRef} send={(el) => sender.send(top, el)} onSent={() => sender.sent(top)} />
        ) : (
          <DeckAction label={onSite ? "Apply on site" : "Apply"} keys="→" onClick={(e) => onAct("apply", undefined, e.currentTarget)} disabled={!top} variant="primary" />
        )}
      </div>
    </div>
  );
}

/** A deck button with its key. Given `send`, it's the SendButton for a job Headcount sends. */
function DeckAction({
  label,
  keys,
  variant,
  onClick,
  disabled,
  send,
  onSent,
  ref,
}: {
  label: string;
  keys: string;
  variant: "primary" | "secondary";
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  send?: (from: HTMLButtonElement) => Promise<boolean>;
  onSent?: () => void;
  ref?: React.Ref<HTMLButtonElement>;
}) {
  const body = (
    <>
      {label}
      <Kbd
        keys={keys}
        className={cn("ml-1 hidden md:inline-flex", variant === "primary" && "[&_kbd]:border-white/30 [&_kbd]:bg-white/15 [&_kbd]:text-on-primary")}
      />
    </>
  );
  const props = { size: "lg", variant, ref, title: `${label} (${keys})`, className: "w-full px-3" } as const;
  return send ? (
    <SendButton {...props} send={send} onSent={onSent}>
      {body}
    </SendButton>
  ) : (
    <Button {...props} onClick={onClick} disabled={disabled}>
      {body}
    </Button>
  );
}

function CaughtUp() {
  const [, navigate] = useLocation();
  return (
    <Card>
      <EmptyState
        art="night"
        title="You're caught up."
        text="Headcount finds new jobs through the day. Check back later, or browse everything."
        action={
          <>
            <Button onClick={() => navigate("/jobs")}>Browse all jobs</Button>
            <Button variant="secondary" onClick={() => navigate("/jobs/saved")}>
              See your saved jobs
            </Button>
          </>
        }
      />
    </Card>
  );
}

/** The floating bottom dock: the undo bar after an action. */
function Dock({ bar, onUndo }: { bar: { key: number; text: string; saved: boolean } | null; onUndo?: () => void }) {
  const showUndo = !!(bar && onUndo);
  return (
    <div
      className="pointer-events-none sticky bottom-[calc(env(safe-area-inset-bottom)+5.75rem)] z-30 flex min-h-12 items-end justify-center md:bottom-6"
      aria-live="polite"
    >
      <AnimatePresence initial={false} mode="popLayout">
        {showUndo && (
          <motion.div
            key={bar!.key}
            initial={{ opacity: 0, y: 24, scale: 0.94 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.96 }}
            transition={spring}
            className="pointer-events-auto flex h-12 max-w-full items-center gap-1 rounded-card bg-accent py-1 pl-4 pr-1 text-sm font-medium text-on-accent shadow-raised"
          >
            <span className="min-w-0 truncate">{bar!.text}</span>
            {bar!.saved && (
              <Link
                href="/jobs/saved"
                className="ml-1 shrink-0 rounded-control px-3 py-2 font-semibold text-white/80 transition-[background-color,color] duration-150 hover:bg-white/10 hover:text-white"
              >
                View
              </Link>
            )}
            <button
              type="button"
              onClick={onUndo}
              className="min-h-10 shrink-0 rounded-control px-3.5 font-semibold transition-[background-color,scale] duration-150 hover:bg-white/10 active:scale-[0.96]"
            >
              Undo
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
